// The account profile (/app/profile): which details a customer can change,
// and the one set of rules for them — the dashboard checks a form with
// these before sending it, and the server checks again before saving
// (server/src/auth/profile.ts). The email is not here on purpose: it comes
// from Google sign-in, and payments and admin rights are matched on it, so
// it can't be changed from the profile page.

export const PROFILE_FIELDS = ['name', 'company', 'phone', 'website', 'country'] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number];

/** The editable part of a profile. Everything but the name is optional;
 * null = not given. */
export interface ProfileDetails {
  name: string;
  company: string | null;
  phone: string | null;
  website: string | null;
  country: string | null;
}

export const PROFILE_MAX_LENGTH: Record<ProfileField, number> = {
  name: 80,
  company: 100,
  phone: 30,
  website: 200,
  country: 2,
};

/** ISO 3166-1 alpha-2. The dashboard shows each one by name in the
 * visitor's own language (Intl.DisplayNames). */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;

const COUNTRY_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

// This package is typed against plain ES2022 (no DOM or Workers types),
// but both places it runs have URL; this is the part of it used here.
declare const URL: new (input: string) => {
  protocol: string;
  hostname: string;
  username: string;
  password: string;
  pathname: string;
  search: string;
  hash: string;
  toString(): string;
};

function optional(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed : null;
}

/** "example.com" → "https://example.com". null when it isn't a web address. */
export function normalizeWebsite(value: string): string | null {
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
  let url: InstanceType<typeof URL>;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!url.hostname.includes('.') || url.username || url.password) return null;
  const s = url.toString();
  // URL adds a trailing slash to a bare domain; "https://example.com" reads better.
  return url.pathname === '/' && !url.search && !url.hash ? s.replace(/\/$/, '') : s;
}

export type ProfileValidation = { ok: true; value: ProfileDetails } | { ok: false; error: string; field: ProfileField };

/** Checks and tidies a submitted profile (trimmed, website given a scheme,
 * country upper-cased). The error is one sentence a customer can act on. */
export function validateProfile(input: Partial<Record<ProfileField, unknown>>): ProfileValidation {
  const name = optional(input.name);
  if (!name) return { ok: false, field: 'name', error: 'Enter your name.' };
  if (name.length > PROFILE_MAX_LENGTH.name) {
    return { ok: false, field: 'name', error: `Your name can be up to ${PROFILE_MAX_LENGTH.name} characters.` };
  }

  const company = optional(input.company);
  if (company && company.length > PROFILE_MAX_LENGTH.company) {
    return { ok: false, field: 'company', error: `The company name can be up to ${PROFILE_MAX_LENGTH.company} characters.` };
  }

  const phone = optional(input.phone);
  if (phone) {
    const digits = phone.replace(/\D/g, '').length;
    if (phone.length > PROFILE_MAX_LENGTH.phone || !/^\+?[0-9 ()./-]+$/.test(phone) || digits < 6 || digits > 15) {
      return { ok: false, field: 'phone', error: 'Enter a phone number with its country code, like +1 555 123 4567.' };
    }
  }

  const websiteInput = optional(input.website);
  let website: string | null = null;
  if (websiteInput) {
    website = normalizeWebsite(websiteInput);
    if (!website || website.length > PROFILE_MAX_LENGTH.website) {
      return { ok: false, field: 'website', error: 'Enter your website, like https://example.com.' };
    }
  }

  const countryInput = optional(input.country);
  const country = countryInput ? countryInput.toUpperCase() : null;
  if (country && !COUNTRY_SET.has(country)) return { ok: false, field: 'country', error: 'Choose your country from the list.' };

  return { ok: true, value: { name, company, phone, website, country } };
}
